/**
 * Autism DSM evidence-decision colour scale.
 *
 * Clinical convention across Adult Autism SSI and Behavioural Observation:
 *   Clear evidence   = red   (strong presence of autistic features)
 *   Partial evidence = amber
 *   No evidence      = green (absence of those features)
 *   Inconclusive     = grey
 *
 * Keep this the single source of truth. Criterion-panel buttons, review-page
 * pills, MDT summary pills, and the OBS evidence-table column headers all
 * derive from these tokens so the scale cannot drift between modules.
 */

/** Full palette for selectable evidence buttons / dropdowns. */
export const AUTISM_EVIDENCE_BUTTON_COLOURS = {
  'Clear evidence': {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-300',
    ring: 'ring-red-200',
    selectedBg: 'bg-red-500',
    selectedBorder: 'border-red-500',
    hoverBorder: 'hover:border-red-400',
  },
  'Partial evidence': {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-300',
    ring: 'ring-amber-200',
    selectedBg: 'bg-amber-500',
    selectedBorder: 'border-amber-500',
    hoverBorder: 'hover:border-amber-400',
  },
  'No evidence': {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-300',
    ring: 'ring-emerald-200',
    selectedBg: 'bg-emerald-600',
    selectedBorder: 'border-emerald-600',
    hoverBorder: 'hover:border-emerald-400',
  },
  'Inconclusive': {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-400',
    ring: 'ring-gray-200',
    selectedBg: 'bg-gray-500',
    selectedBorder: 'border-gray-500',
    hoverBorder: 'hover:border-gray-400',
  },
};

/**
 * Compact filled pills used in SSI / MDT evidence summary tables.
 * Built from the button tokens' selectedBg so pill and button scales cannot
 * diverge.
 */
export const AUTISM_EVIDENCE_PILL_COLOURS = {
  'Clear evidence':   { label: 'Clear evidence',   bg: AUTISM_EVIDENCE_BUTTON_COLOURS['Clear evidence'].selectedBg,   text: 'text-white' },
  'Partial evidence': { label: 'Partial evidence', bg: AUTISM_EVIDENCE_BUTTON_COLOURS['Partial evidence'].selectedBg, text: 'text-white' },
  'No evidence':      { label: 'No evidence',      bg: AUTISM_EVIDENCE_BUTTON_COLOURS['No evidence'].selectedBg,      text: 'text-white' },
  'Inconclusive':     { label: 'Inconclusive',     bg: AUTISM_EVIDENCE_BUTTON_COLOURS.Inconclusive.selectedBg,       text: 'text-white' },
};

/**
 * Column header / cell tinting for the OBS task-count evidence table.
 * Header uses the same selected fill as the buttons; cells use the soft bg tint.
 */
export const AUTISM_EVIDENCE_COLUMN_STYLES = {
  'Clear evidence':   {
    headerBg: AUTISM_EVIDENCE_BUTTON_COLOURS['Clear evidence'].selectedBg,
    cellBg: AUTISM_EVIDENCE_BUTTON_COLOURS['Clear evidence'].bg,
    dot: 'text-red-400',
  },
  'Partial evidence': {
    headerBg: AUTISM_EVIDENCE_BUTTON_COLOURS['Partial evidence'].selectedBg,
    cellBg: AUTISM_EVIDENCE_BUTTON_COLOURS['Partial evidence'].bg,
    dot: 'text-amber-500',
  },
  'No evidence': {
    headerBg: AUTISM_EVIDENCE_BUTTON_COLOURS['No evidence'].selectedBg,
    cellBg: AUTISM_EVIDENCE_BUTTON_COLOURS['No evidence'].bg,
    dot: 'text-emerald-500',
  },
  'Inconclusive': {
    headerBg: AUTISM_EVIDENCE_BUTTON_COLOURS.Inconclusive.selectedBg,
    cellBg: 'bg-gray-50',
    dot: 'text-gray-400',
  },
};
